// Copyright (c) 2025 Nebularis Holdings LLC
// Licensed to Nutrx Solutions under a non-exclusive, non-transferable license.
// See IP License Agreement for usage restrictions.

// frontend/src/components/mission-execution/tabs/SOPExecutionTab.jsx

import React from "react";
import ExecutionSection from "../shared/ExecutionSection";
import ExecutionEmptyState from "../shared/ExecutionEmptyState";
import ExecutionActionRow from "../shared/ExecutionActionRow";
import ExecutionMetricTile from "../shared/ExecutionMetricTile";
import ExecutionContextCard from "../shared/ExecutionContextCard";

function displayValue(value, fallback = "—") {
  if (value === null || value === undefined || value === "") return fallback;
  if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") return String(value);
  if (Array.isArray(value)) {
    const text = value.map((item) => displayValue(item, "")).filter(Boolean).join(", ");
    return text || fallback;
  }
  if (typeof value === "object") {
    const candidate = value.label || value.title || value.name || value.value || value.status || value.state || value.id;
    if (candidate !== undefined && candidate !== null && candidate !== value) return displayValue(candidate, fallback);
    try {
      return JSON.stringify(value);
    } catch (error) {
      return fallback;
    }
  }
  return String(value);
}

function clean(value) {
  const text = String(value ?? "").trim();
  return text || "";
}

function humanize(value, fallback = "—") {
  const raw = clean(displayValue(value, ""));
  if (!raw) return fallback;

  const normalized = raw.replace(/_/g, " ").replace(/\s+/g, " ").trim();
  const lower = normalized.toLowerCase();
  const exact = {
    soc2: "SOC2",
    "soc 2": "SOC2",
    sop: "SOP",
    aws: "AWS",
    s3: "S3",
    iam: "IAM",
    waf: "WAF",
    api: "API",
    draft: "Draft",
    approved: "Approved",
    active: "Active",
    archived: "Archived",
    "pending approval": "Pending Approval",
    "not linked": "Not Linked",
    "no safe sop linked": "No Safe SOP Linked",
    "safe fallback": "Safe Fallback",
    "control link": "Control Link",
    "rule link": "Rule Link",
    "task scoped": "Task Scoped",
  };

  if (exact[lower]) return exact[lower];

  return normalized
    .split(" ")
    .map((word) => {
      const key = word.toLowerCase();
      if (exact[key]) return exact[key];
      if (/^cc\d+(?:\.\d+)?$/i.test(word)) return word.toUpperCase();
      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    })
    .join(" ");
}

function rowsFromContext(sopContext) {
  if (Array.isArray(sopContext?.sops)) return sopContext.sops.filter(Boolean);
  if (Array.isArray(sopContext?.items)) return sopContext.items.filter(Boolean);
  if (sopContext?.primary_sop) return [sopContext.primary_sop];
  if (sopContext?.sop) return [sopContext.sop];
  return [];
}

function fallbackSteps(task = {}, sopContext = {}) {
  const fromContext = Array.isArray(sopContext?.fallback_guidance?.steps) ? sopContext.fallback_guidance.steps : [];
  const fromTask = Array.isArray(task?.sop_fallback_steps) ? task.sop_fallback_steps : [];
  const fromRequired = Array.isArray(task?.validation_summary?.required_actions) ? task.validation_summary.required_actions : [];
  const steps = [...fromContext, ...fromTask, ...fromRequired].filter(Boolean);

  if (steps.length) return steps.slice(0, 6);

  return [
    "Review the task remediation guidance before changing state.",
    "Complete only the remediation steps that apply to this task/control/resource.",
    "Capture or refresh task-scoped evidence.",
    "Run validation before preparing the task for closure.",
  ];
}

function sopTitle(row = {}) {
  const sop = row.sop || row;
  return displayValue(sop.title || sop.name || sop.code || sop.sop_id, "SOP record");
}

function sopStatus(row = {}) {
  const sop = row.sop || row;
  return sop.status || sop.current_version_status || row.status || "Linked";
}

function sopVersion(row = {}) {
  const sop = row.sop || row;
  return sop.latest_version_number || sop.current_version_number || sop.version_number || sop.version || sop.current_version?.version_number;
}

function sopOwner(row = {}) {
  const sop = row.sop || row;
  return sop.owner_name || sop.owner || sop.owner_user_id || sop.team;
}

function sopContextText(row = {}) {
  const sop = row.sop || row;
  const link = row.link || sop.link || {};
  const outdated = sop.is_outdated === true || row.is_outdated === true;

  if (outdated) {
    return displayValue(sop.outdated_reason || row.outdated_reason, "This SOP may need review before operational use.");
  }

  return displayValue(
    link.notes || sop.description || row.description,
    "This SOP is linked to the selected task/control/resource."
  );
}

export default function SOPExecutionTab({
  task = {},
  sopContext = null,
  sopLoading = false,
  sopError = "",
  onRefreshSOP,
  onOpenTab,
}) {
  const rows = rowsFromContext(sopContext);
  const primary = sopContext?.primary_sop || rows[0] || null;
  const linked = rows.length > 0 || Boolean(task.sop_title || task.sop_name || task.linked_sop);
  const status = sopContext?.status || (linked ? "linked" : "no_safe_sop_linked");
  const versionCount = rows.filter((row) => sopVersion(row)).length;
  const fallback = fallbackSteps(task, sopContext);

  return (
    <>
      <ExecutionSection
        title="SOP Guidance"
        description="Task-scoped SOP context for the selected task/control/resource. Unrelated SOPs are intentionally excluded."
        badge={sopLoading ? "Loading" : linked ? humanize(status, "Linked") : "No Safe SOP Linked"}
        badgeTone={linked ? "ok" : "warn"}
      >
        <div style={{ display: "grid", gridTemplateColumns: "repeat(4, minmax(0, 1fr))", gap: 14 }}>
          <ExecutionMetricTile label="Linked SOPs" value={rows.length} />
          <ExecutionMetricTile label="Primary SOP" value={primary ? "Linked" : "Not Linked"} color={primary ? "#34d399" : "#facc15"} />
          <ExecutionMetricTile label="Versions" value={versionCount || "—"} color="#60a5fa" />
          <ExecutionMetricTile label="Guidance Mode" value={linked ? "SOP Linked" : "Safe Fallback"} color={linked ? "#34d399" : "#facc15"} />
        </div>

        {sopError ? (
          <p style={{ color: "#fca5a5", fontSize: 13, lineHeight: 1.55, margin: 0 }}>
            <strong>SOP load issue:</strong> {sopError}
          </p>
        ) : null}

        <p style={{ color: "#93c5fd", fontSize: 12.5, lineHeight: 1.5, margin: 0 }}>
          This tab only shows SOPs linked by task, control, rule, or framework context. If no safe SOP is linked, use the fallback steps below.
        </p>

        <ExecutionActionRow
          actions={[
            {
              key: "refresh-sop",
              label: sopLoading ? "Refreshing…" : "Refresh SOP",
              onClick: onRefreshSOP,
              disabled: sopLoading || !onRefreshSOP,
              variant: "primary",
            },
            { key: "guidance", label: "Open Guidance", onClick: () => onOpenTab?.("guidance") },
            { key: "history", label: "View History", onClick: () => onOpenTab?.("history") },
          ]}
        />
      </ExecutionSection>

      <ExecutionSection
        title={linked ? "Linked SOP Records" : "Safe Fallback Guidance"}
        description={
          linked
            ? "SOPs linked to this task/control/resource, ordered by specificity and priority."
            : "No safe SOP is linked to this task/control yet. Manual guidance remains task-scoped."
        }
        badge={linked ? `${rows.length} record(s)` : "Manual"}
        badgeTone={linked ? "ok" : "warn"}
      >
        {sopLoading && !rows.length ? (
          <ExecutionEmptyState title="Loading SOP context" message="OASYS is loading task-scoped SOP guidance for this work item." />
        ) : null}

        {rows.length
          ? rows.map((row, index) => {
              const sop = row.sop || row;
              const link = row.link || sop.link || {};
              const isOutdated = sop.is_outdated === true || row.is_outdated === true;

              return (
                <ExecutionContextCard
                  key={displayValue(sop.sop_id || sop.id || row.sop_id || row.id, `sop-${index}`)}
                  title={sopTitle(row)}
                  chips={[
                    { label: humanize(sopStatus(row), "Linked"), tone: isOutdated ? "danger" : "default" },
                    { label: link.rule_id ? "Rule Link" : "Control Link", tone: "default" },
                    { label: humanize(link.framework || sop.framework || task.framework, "Framework"), tone: "neutral" },
                  ]}
                  meta={[
                    { label: "SOP ID", value: sop.sop_id || sop.id || row.sop_id, wrap: true },
                    { label: "Version", value: sopVersion(row), wrap: true },
                    { label: "Owner", value: sopOwner(row), wrap: true },
                    { label: "Control", value: link.control_id || task.control_id || task.control, wrap: true },
                    { label: "Rule", value: link.rule_id || task.rule_id, wrap: true },
                    { label: "Effective", value: sop.effective_date || sop.latest_version?.effective_date, wrap: true },
                  ]}
                  context={sopContextText(row)}
                  actions={[
                    { key: "guidance", label: "Open Guidance", onClick: () => onOpenTab?.("guidance") },
                    { key: "history", label: "View History", onClick: () => onOpenTab?.("history") },
                  ]}
                />
              );
            })
          : null}

        {!sopLoading && !rows.length ? (
          <>
            <ExecutionEmptyState
              title="No safe SOP is linked to this task/control yet."
              message="OASYS will not recommend unrelated SOPs. Use the task-specific fallback steps until an approved SOP is linked."
            />
            <div style={{ color: "#bfdbfe", fontSize: 13, lineHeight: 1.6, margin: 0 }}>
              <strong style={{ color: "#f8fafc" }}>Fallback Steps</strong>
              <ol style={{ margin: "8px 0 0", paddingLeft: 20 }}>
                {fallback.map((step, index) => (
                  <li key={`sop-fallback-${index}`}>{displayValue(step)}</li>
                ))}
              </ol>
            </div>
          </>
        ) : null}
      </ExecutionSection>
    </>
  );
}
